/**
 * Display helpers.
 *
 * Money arrives as integer cents (§16.13) and is formatted here, at the one
 * boundary allowed to turn it into something a person reads. Nothing in the
 * portal does arithmetic on it.
 */

export function money(cents: number, currency = 'KES'): string {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

export function date(iso: string | null): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeZone: 'Africa/Nairobi',
  }).format(new Date(iso))
}

export function dateTime(iso: string | null): string {
  if (!iso) return 'never'
  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Africa/Nairobi',
  }).format(new Date(iso))
}

/** "in 12 days" / "26 days ago" — the thing an owner actually wants to know. */
export function relativeDays(count: number, past: boolean): string {
  if (count === 0) return past ? 'today' : 'today'
  const unit = count === 1 ? 'day' : 'days'
  return past ? `${count} ${unit} ago` : `${count} ${unit}`
}

/** Installer sizes only. One decimal is as much as anyone reads.
 */
export function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * "2 hours ago" / "9 days ago", from an age already measured in hours.
 *
 * Hours rather than a timestamp difference, because the server has already
 * done the arithmetic against its own clock — recomputing it here against the
 * browser's would disagree with it whenever a shop PC's clock is wrong, which
 * is exactly the situation this text is describing.
 */
export function relativeHours(hours: number | null): string {
  if (hours === null) return 'never'
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.floor(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}
